/**
 * Every platform capability (purchases, ads, attribution, crash, consent) is a *slot*: the shared
 * game calls the slot, and the boot sequence installs the adapter that fits the platform. When
 * nothing is installed, calls return `{ ok: false, reason: "unsupported" }` instead of throwing,
 * so gameplay code never branches on vendor SDKs and never crashes where a feature is absent.
 */
export type CapabilityResult<T> =
  | { ok: true; value: T }
  | { ok: false; reason: "unsupported" | "unavailable" | "cancelled" | "error"; detail?: string };

export function ok<T>(value: T): CapabilityResult<T> {
  return { ok: true, value };
}

/** @public */
export function fail<T = never>(
  reason: Exclude<CapabilityResult<T>, { ok: true }>["reason"],
  detail?: string,
): CapabilityResult<T> {
  return detail === undefined ? { ok: false, reason } : { ok: false, reason, detail };
}

export const UNSUPPORTED = fail("unsupported");

export interface Slot<A> {
  readonly name: string;
  /** The installed adapter, or the fallback that answers `unsupported`. */
  get(): A;
  /** Install an adapter, once per platform. */
  use(adapter: A): void;
  installed(): boolean;
}

export function createSlot<A>(name: string, fallback: A): Slot<A> {
  let current: A = fallback;
  let isInstalled = false;
  return {
    name,
    get: () => current,
    use(adapter) {
      current = adapter;
      isInstalled = true;
    },
    installed: () => isInstalled,
  };
}
